import React, { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import {
  Button,
  Row,
  Select,
  Space,
  Spinner
} from '@masterteam/mt-design-react';

import Modal from '../Modal';
import CardList from '../CardList';
import AdminPage from '../AdminPage';
import KPIWizard from '../KPIWizard';
import DeleteModal from '../DeleteModal';
import LockActionModal from '../LockActionModal';

import { ReactComponent as EditIcon } from '../../assets/images/edit.svg';
import { ReactComponent as LockIcon } from '../../assets/images/lock.svg';
import { ReactComponent as DeleteIcon } from '../../assets/images/delete.svg';
import { ReactComponent as UnlockIcon } from '../../assets/images/unlock.svg';

import api from '../../providers/api';
import { addToast } from '../../helpers';

import './style.scss';

const initialModalState = {
  isOpen: false,
  data: null,
  loading: false
};

function KpisPage() {
  const intl = useIntl();
  const isAr = intl.locale === 'ar';
  const [strategyTypes, objectiveLevels] = useSelector(({ theme }) => [
    theme.StrategyTypes.item.Items,
    theme.ObjectiveLevel.item.Items
  ]);
  const [kpiModal, setKpiModal] = useState(initialModalState);
  const [deleteModal, setDeleteModal] = useState(initialModalState);
  const [lockModal, setLockModal] = useState(initialModalState);
  const [objectivesList, setObjectivesList] = useState([]);
  const [strategiesWithThemes, setStrategiesWithThemes] = useState([]);
  const [strategiesWithPillars, setStrategiesWithPillars] = useState([]);
  const [selectedStrategy, setSelectedStrategy] = useState(0);
  const [selectedItem, setSelectedItem] = useState(0);
  const [selectedType, setSelectedType] = useState(strategyTypes[0]);
  const [selectedLevel, setSelectedLevel] = useState(objectiveLevels[0]);
  const [selectedObjective, setSelectedObjective] = useState(0);
  const [loadingSo, setLoadingSo] = useState(true);
  const [loadingFilters, setLoadingFilters] = useState(true);

  const getObjectivesWithKPIs = async () => {
    try {
      setLoadingSo(true);
      let params = {};
      if (+selectedType?.Value === 1) {
        params.LevelID = +selectedLevel.Value;
        if (strategiesWithThemes[selectedStrategy]?.Themes[selectedItem]?.ID)
          params.ThemeID =
            strategiesWithThemes[selectedStrategy]?.Themes[selectedItem]?.ID;
      } else {
        if (strategiesWithPillars[selectedStrategy]?.Pillars[selectedItem]?.ID)
          params.PillarID =
            strategiesWithPillars[selectedStrategy]?.Pillars[selectedItem]?.ID;
      }

      const res = await api.objective.getAllWithKPIs(params);

      setObjectivesList(res?.Data || []);
      setSelectedObjective(0);
    } catch (err) {
      addToast.error(err.message, {
        toastId: 'get-users-error'
      });
    } finally {
      setLoadingSo(false);
    }
  };

  const getFilters = async () => {
    try {
      setLoadingFilters(true);
      const [strategiesWithThemesRes, strategiesWithPillarsRes] =
        await Promise.all([
          api.strategy.getAllWithThemes(),
          api.strategy.getAllWithPillars()
        ]);
      setStrategiesWithThemes(strategiesWithThemesRes?.Data || []);
      setStrategiesWithPillars(strategiesWithPillarsRes?.Data || []);
      setSelectedStrategy(0);
    } catch (err) {
      addToast.error(err.message, {
        toastId: 'get-users-error'
      });
    } finally {
      setLoadingFilters(false);
    }
  };

  useEffect(() => {
    getFilters();
  }, []);

  useEffect(() => {
    if (strategiesWithThemes?.length || strategiesWithPillars?.length)
      getObjectivesWithKPIs();
  }, [
    selectedType,
    selectedStrategy,
    selectedItem,
    selectedLevel,
    strategiesWithThemes,
    strategiesWithPillars
  ]);

  const updateKPI = (item) => {
    let newList = [...objectivesList];
    const key = objectivesList.findIndex((e) => e.ID === item.Objective.ID);

    newList = newList.map((e) => {
      return { ...e, KPIs: e.KPIs.filter((kpi) => kpi.ID !== item.ID) };
    });

    newList[key].KPIs = [...newList?.[key].KPIs, item];

    setObjectivesList(newList);
  };

  const removeKPI = (item) => {
    setObjectivesList((list) =>
      list.map((so) => {
        if (so.ID === item.Objective?.ID) {
          const _so = {
            ...so,
            KPIs: so.KPIs.filter((kpi) => kpi.ID !== item.ID)
          };
          return _so;
        }
        return so;
      })
    );
  };

  const handleDelete = (item) => {
    setDeleteModal((currentState) => ({
      ...currentState,
      loading: true
    }));
    api.kpi
      .delete(item.ID)
      .then((res) => {
        const { StatusCode, StatusMessage } = res;
        if (StatusCode === 'Success') {
          setDeleteModal(initialModalState);
          removeKPI(item);
          addToast.success('toast.successDelete');
        } else if (StatusCode === 'fail') {
          setDeleteModal((currentState) => ({
            ...currentState,
            loading: false
          }));
          addToast.error(StatusMessage, {
            toastId: 'get-users-error'
          });
        }
      })
      .catch((err) => {
        setDeleteModal((currentState) => ({
          ...currentState,
          loading: false
        }));
        return err;
      });
  };

  const handleLockAction = (item) => {
    setLockModal((currentState) => ({
      ...currentState,
      loading: true
    }));
    const action = item.IsLocked ? api.kpi.unlock : api.kpi.lock;
    action(item.ID)
      .then((res) => {
        const { StatusCode, StatusMessage } = res;
        if (StatusCode === 'Success') {
          setLockModal(initialModalState);
          updateKPI({ ...item, IsLocked: !item.IsLocked });
          addToast.success(
            item.IsLocked ? 'kpi.unlock-success' : 'kpi.lock-success'
          );
        } else if (StatusCode === 'fail') {
          setLockModal((currentState) => ({
            ...currentState,
            loading: false
          }));
          addToast.error(StatusMessage, {
            toastId: 'get-users-error'
          });
        }
      })
      .catch((err) => {
        setLockModal((currentState) => ({
          ...currentState,
          loading: false
        }));
        return err;
      });
  };

  return (
    <AdminPage
      title={intl.formatMessage({ id: 'common.kpis' })}
      loading={loadingFilters}
    >
      <Space
        direction="vertical"
        size="medium"
        className="admin-kpis-container"
      >
        <Space
          style={{
            width: '75%',
            display: 'grid',
            gridTemplateColumns: 'repeat(4,1fr)'
          }}
        >
          <Select
            options={strategyTypes}
            getOptionValue={(item) => item.Value}
            getOptionLabel={(item) => (isAr ? item.Arabic : item.English)}
            placeholder={intl.formatMessage({ id: 'strategy.strategy-type' })}
            value={selectedType}
            onChange={(item) => {
              setSelectedType(item);
              setSelectedStrategy(0);
              setSelectedItem(0);
              setSelectedLevel(objectiveLevels[0]);
            }}
          />

          <Select
            options={
              +selectedType?.Value === 1
                ? strategiesWithThemes
                : strategiesWithPillars
            }
            getOptionValue={(item) => item.ID}
            getOptionLabel={(item) =>
              isAr ? item.ArabicName : item.EnglishName
            }
            placeholder={intl.formatMessage({ id: 'common.strategy' })}
            value={
              (+selectedType?.Value === 1
                ? strategiesWithThemes[selectedStrategy]
                : strategiesWithPillars[selectedStrategy]) || null
            }
            onChange={(item) => {
              const strategies =
                +selectedType?.Value === 1
                  ? strategiesWithThemes
                  : strategiesWithPillars;

              setSelectedStrategy(
                strategies.findIndex((i) => i.ID === item.ID)
              );
              setSelectedItem(0);
            }}
          />

          <Select
            options={
              (+selectedType?.Value === 1
                ? strategiesWithThemes[selectedStrategy]?.Themes
                : strategiesWithPillars[selectedStrategy]?.Pillars) || []
            }
            getOptionValue={(item) => item.ID}
            getOptionLabel={(item) =>
              isAr ? item.ArabicName : item.EnglishName
            }
            placeholder={intl.formatMessage({
              id: +selectedType?.Value === 1 ? 'common.theme' : 'common.pillar'
            })}
            value={
              (+selectedType?.Value === 1
                ? strategiesWithThemes[selectedStrategy]?.Themes?.[selectedItem]
                : strategiesWithPillars[selectedStrategy]?.Pillars?.[
                    selectedItem
                  ]) || null
            }
            onChange={(item) =>
              setSelectedItem(
                +selectedType?.Value === 1
                  ? strategiesWithThemes[selectedStrategy]?.Themes?.findIndex(
                      (i) => i.ID === item.ID
                    )
                  : strategiesWithPillars[selectedStrategy]?.Pillars?.findIndex(
                      (i) => i.ID === item.ID
                    )
              )
            }
          />

          {+selectedType?.Value === 1 ? (
            <Select
              options={objectiveLevels}
              getOptionValue={(item) => item.Value}
              getOptionLabel={(item) => (isAr ? item.Arabic : item.English)}
              placeholder={intl.formatMessage({
                id: 'common.level'
              })}
              value={selectedLevel}
              onChange={(item) => {
                setSelectedLevel(item);
              }}
            />
          ) : null}
        </Space>

        {loadingSo ? (
          <Row justify="center">
            <Spinner size="large" />
          </Row>
        ) : (
          <Row size="large" className="d-flex kpi-d-flex">
            <div style={{ flex: 1 }}>
              <CardList
                data={objectivesList}
                title={intl.formatMessage({
                  id: 'common.strategic-objective'
                })}
                onClickItem={(item) => {
                  setSelectedObjective(
                    objectivesList?.findIndex((e) => e.ID === item.ID)
                  );
                }}
                nameKey={isAr ? 'ArabicName' : 'EnglishName'}
                valueKey="ID"
                selectedItem={objectivesList?.[selectedObjective]?.ID}
              />
            </div>

            <div
              className="animated fadeInRight"
              style={{ flex: 2, height: 'calc(100vh - 400px)' }}
            >
              <CardList
                data={objectivesList?.[selectedObjective]?.KPIs}
                title={intl.formatMessage(
                  { id: 'common.list' },
                  {
                    value: intl.formatMessage({ id: 'common.kpis' })
                  }
                )}
                nameKey={isAr ? 'ArabicName' : 'EnglishName'}
                valueKey="ID"
                animation={isAr ? 'fadeInLeft' : 'fadeInRight'}
                headerActions={[
                  {
                    key: 'add',
                    label: intl.formatMessage({ id: 'common.add' }),
                    onClick: () =>
                      setKpiModal({
                        isOpen: true
                      })
                  }
                  // {
                  //   key: "reset",
                  //   label: intl.formatMessage({ id: "common.reset" }),
                  //   onClick: () => {
                  //     setSelectedSubSO(null);
                  //     setSelectedSO(null);
                  //     getAllKPIs();
                  //   }
                  // }
                ]}
                actions={[
                  {
                    key: 'edit',
                    variant: 'primary',
                    Icon: EditIcon,
                    tooltip: intl.formatMessage({ id: 'common.update' }),
                    disabled: (item) => !item.IsEditable,
                    onClick: (item) => {
                      setKpiModal({
                        isOpen: true,
                        data: item
                      });
                    }
                  },
                  (item) => (
                    <Button
                      fill="none"
                      shape="square"
                      key="lock"
                      variant="success"
                      Icon={item.IsLocked ? UnlockIcon : LockIcon}
                      tooltip-position={isAr ? 'right' : 'left'}
                      tooltip={
                        !item.AllowLock
                          ? intl.formatMessage({ id: 'kpi.lock-warning' })
                          : intl.formatMessage({
                              id: item.IsLocked
                                ? 'common.unlock'
                                : 'common.lock'
                            })
                      }
                      onClick={() => {
                        setLockModal({
                          isOpen: true,
                          data: item,
                          loading: false
                        });
                      }}
                      disabled={!item.AllowLock}
                    />
                  ),
                  {
                    key: 'delete',
                    variant: 'error',
                    Icon: DeleteIcon,
                    tooltip: intl.formatMessage({ id: 'common.delete' }),
                    onClick: (item) => {
                      setDeleteModal({
                        isOpen: true,
                        data: item,
                        loading: false
                      });
                    },
                    disabled: (item) => !item.IsDeletable
                  }
                ]}
              />
            </div>
          </Row>
        )}
      </Space>

      {kpiModal?.isOpen && (
        <Modal isOpen={kpiModal?.isOpen}>
          <KPIWizard
            bindKPI={(item) => {
              if (
                +selectedType?.Value ===
                +item?.Perspective?.Strategy?.StrategyType
              ) {
                updateKPI(item);
              }
            }}
            editData={kpiModal.data}
            modeSaveOrUpdate={!kpiModal.data}
            closeModal={() => setKpiModal(initialModalState)}
            selectedType={selectedType}
            selectedStrategy={
              +selectedType?.Value === 1
                ? strategiesWithThemes[selectedStrategy]
                : strategiesWithPillars[selectedStrategy]
            }
            selectedTheme={
              +selectedType?.Value === 1
                ? strategiesWithThemes[selectedStrategy]?.Themes?.[selectedItem]
                : null
            }
            selectedPillar={
              +selectedType?.Value === 2
                ? strategiesWithPillars[selectedStrategy]?.Pillars?.[
                    selectedItem
                  ]
                : null
            }
            selectedSO={objectivesList?.[selectedObjective]}
            objectivesList={objectivesList}
            selectedObjective={objectivesList?.[selectedObjective]}
            selectedLevel={selectedLevel}
          />
        </Modal>
      )}

      {deleteModal?.isOpen && (
        <DeleteModal
          isOpen={deleteModal?.isOpen}
          title={intl.formatMessage({ id: 'common.kpi' })}
          closeModal={() => setDeleteModal(initialModalState)}
          handleDelete={() => handleDelete(deleteModal.data)}
          ButtonStatus={deleteModal.loading}
        />
      )}

      {lockModal?.isOpen && (
        <LockActionModal
          title={intl.formatMessage({ id: 'common.kpi' })}
          isOpen={lockModal?.isOpen}
          closeModal={() => setLockModal(initialModalState)}
          handleAction={() => handleLockAction(lockModal.data)}
          ButtonStatus={lockModal.loading}
          isLocked={lockModal.data.IsLocked}
        />
      )}
    </AdminPage>
  );
}

export default KpisPage;
